import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import firebaseApp, { db, getFcmToken, messaging } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const nextPath = new URLSearchParams(location.search).get('next');
  const redirectPath = nextPath && nextPath.startsWith('/') && !nextPath.startsWith('//') ? nextPath : '/dashboard';
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

const handleLogin = async (e) => {
  e.preventDefault();
  setError("");

  if (!phoneNumber || !password) {
    setError("দয়া করে ফোন নম্বর এবং পাসওয়ার্ড লিখুন");
    return;
  }

  setIsLoading(true);

  try {
    let digitsOnly = phoneNumber.replace(/\D/g, "");

    if (digitsOnly.startsWith("880")) {
      digitsOnly = digitsOnly.substring(3);
    }

    if (digitsOnly.startsWith("0")) {
      digitsOnly = digitsOnly.substring(1);
    }

    if (digitsOnly.length !== 10) {
      setIsLoading(false);
      setError("১১ ডিজিটের সঠিক মোবাইল নম্বর লিখুন (যেমন: 017XXXXXXXX)");
      return;
    }

    const formattedPhone = `+880${digitsOnly}`;

    const userDocRef = doc(db, "users", formattedPhone);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      setIsLoading(false);
      setError("এই মোবাইল নম্বরে কোনো অ্যাকাউন্ট পাওয়া যায়নি! আগে রেজিস্টার করুন।");
      return;
    }

    const userData = userDocSnap.data();

    if (userData.password === password && typeof userData.role === 'string' && userData.role.trim()) {
      const createApiSession = httpsCallable(getFunctions(firebaseApp, 'us-central1'), 'createDeveloperApiSession');
      const sessionResult = await createApiSession({ phoneNumber: formattedPhone, password });

      // Login Session
      localStorage.removeItem("nexora_user_uid");
      localStorage.setItem("nexora_user_uid", formattedPhone);
      sessionStorage.setItem("nexora_api_session", sessionResult.data.sessionToken);

      let savedToken = null;

      // ==========================
      // Android Native App
      // ==========================
      try {

        if (
          window.AndroidAudio &&
          typeof window.AndroidAudio.syncUserToken === "function"
        ) {
          window.AndroidAudio.syncUserToken(formattedPhone);
        }

        if (
          window.AndroidAudio &&
          typeof window.AndroidAudio.getFcmToken === "function"
        ) {

          const token = window.AndroidAudio.getFcmToken();

          console.log("Android Token:", token);

          if (token && token.length > 20) {

            savedToken = token;

            if (
              typeof window.AndroidAudio.saveFcmToken === "function"
            ) {
              window.AndroidAudio.saveFcmToken(
                formattedPhone,
                token
              );
            }

            await updateDoc(
              doc(db, "users", formattedPhone),
              {
                fcmToken: token,
                deviceType: "android",
                lastLogin: new Date()
              }
            );

            console.log("Android Token Saved");
          }

        }

      } catch (err) {
        console.warn("Android Bridge Error:", err);
      }

      // ==========================
      // Chrome Browser
      // ==========================
      if (!savedToken) {

        try {

          if (
            messaging &&
            "Notification" in window
          ) {

            const permission =
              await Notification.requestPermission();

            if (
              permission === "granted" &&
              "serviceWorker" in navigator
            ) {

              const registration =
                await navigator.serviceWorker.ready;

              const webToken =
                await getFcmToken(registration);

              if (webToken) {

                await updateDoc(
                  doc(db, "users", formattedPhone),
                  {
                    fcmToken: webToken,
                    deviceType: "web",
                    lastLogin: new Date()
                  }
                );

                console.log("Web Token Saved");
              }

            }

          }

        } catch (err) {
          console.warn("Web FCM Error:", err);
        }

      }

      setIsLoading(false);
      navigate(redirectPath, { replace: true });

    } else {

      setIsLoading(false);
      setError("ফোন নম্বর, পাসওয়ার্ড অথবা অ্যাকাউন্টের রোল সঠিক নয়।");

    }

  } catch (err) {

    console.error(err);

    setIsLoading(false);

    setError(
      "লগইন করতে সমস্যা হয়েছে: " + err.message
    );

  }
};

  return (
    <div className="login-screen" style={styles.container}>
      <style>{`
        * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* =========================================================
           NEXORA CENTRAL THEME SYSTEM
           Settings/App-level theme can switch:
           document.documentElement.setAttribute('data-theme', 'dark')
           or 'light'. This page does not depend on phone theme.
           ========================================================= */
        :root {
          --nexora-bg: #f5f8f4;
          --nexora-surface: #ffffff;
          --nexora-card: #ffffff;
          --nexora-input: #f8fafc;
          --nexora-text: #0f172a;
          --nexora-secondary-text: #64748b;
          --nexora-label: #475569;
          --nexora-border: #dfe9e2;
          --nexora-input-border: #d6e2d9;
          --nexora-divider: #dfe9e2;
          --nexora-prefix-border: #a8bcae;
          --nexora-error-bg: #fef2f2;
          --nexora-error-border: #fecaca;
          --nexora-error-text: #ef4444;
          --nexora-accent: #62b875;
          --nexora-accent-hover: #267c48;
          --nexora-accent-shadow: rgba(38, 124, 72, 0.16);
          --nexora-focus-shadow: rgba(38, 124, 72, 0.12);
        }

        .login-screen { position: relative; isolation: isolate; min-height: 100dvh; padding: 28px 18px; background-color: var(--nexora-bg); background-image: linear-gradient(rgba(16,37,29,.035) 1px,transparent 1px),linear-gradient(90deg,rgba(16,37,29,.035) 1px,transparent 1px); background-size: 26px 26px; }
        .login-card { width: 100%; max-width: 420px; background: var(--nexora-card); border-radius: 10px; padding: 32px 28px 24px; box-shadow: 0 18px 50px rgba(27,72,44,.12); border: 1px solid var(--nexora-border); z-index: 10; }
        .input-field { width: 100%; padding: 14px 16px; background-color: var(--nexora-input); border: 1px solid var(--nexora-input-border); border-radius: 6px; font-size: 14px; color: var(--nexora-text); outline: none; transition: all 0.2s; }
        
        /* Nexora-এর Green থিম অনুযায়ী ফোকাস কালার */
        .input-field:focus { border-color: var(--nexora-accent); background-color: var(--nexora-surface); box-shadow: 0 0 0 4px var(--nexora-focus-shadow); }
        
        /* Green বাটন */
        .btn-login { width: 100%; min-height: 46px; padding: 12px 16px; background: var(--nexora-accent); border: none; border-radius: 6px; color: #082513; font-size: 13px; font-weight: 800; cursor: pointer; box-shadow: 0 8px 20px var(--nexora-accent-shadow); transition: background 0.2s; }
        .btn-login:hover { background: var(--nexora-accent-hover); }
        .btn-login:disabled { opacity: 0.7; cursor: not-allowed; }
        .login-card h1 { font-family: Arial, Helvetica, sans-serif; font-size: 27px !important; line-height: 1.15; color: #10251d !important; }
        .login-card input:focus { border-color: #267c48; background: #fff; box-shadow: 0 0 0 3px var(--nexora-focus-shadow); }
        .login-card a { color: #267c48 !important; }
        @media (max-width: 480px) { .login-screen { padding: 16px; } .login-card { padding: 26px 20px 20px; } }
        
        .spinner { width: 20px; height: 20px; border: 2.5px solid rgba(255,255,255,0.3); border-top-color: #ffffff; border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="login-card">
        <div style={styles.headerGroup}>
          {/* Public ফোল্ডার থেকে লোগো */}
          <img src="/logo.png" alt="Nexora Logo" style={styles.logoImage} />
          
          <h1 style={styles.title}>Welcome Back</h1>
          <p style={styles.subtitle}>আপনার মোবাইল নম্বর ও পাসওয়ার্ড দিয়ে লগইন করুন</p>
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <form onSubmit={handleLogin}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>PHONE NUMBER</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={styles.prefix}>+880</span>
              <input
                type="tel"
                placeholder="017XXXXXXXX"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="input-field"
                style={{ paddingLeft: '70px' }}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>PASSWORD</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                style={{ paddingRight: '60px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.toggleBtn}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="btn-login">
            {isLoading ? <div className="spinner" /> : 'Login to Nexora'}
          </button>
        </form>

        <div style={styles.footer}>
          <p style={styles.footerText}>
            Don't have an account? <Link to="/register" style={{ color: 'var(--nexora-accent)', fontWeight: '700', textDecoration: 'none' }}>Register Now</Link>
          </p>
          <Link to="/privacy-policy" style={styles.privacyLink}>Privacy &amp; Policy</Link>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100dvh', backgroundColor: 'var(--nexora-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  headerGroup: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px', textAlign: 'center' },
  
  // লোগো ইমেজের স্টাইল
  logoImage: { width: '70px', height: 'auto', marginBottom: '12px', objectFit: 'contain' },
  
  title: { fontSize: '22px', fontWeight: '800', color: 'var(--nexora-text)', margin: '0 0 4px' },
  subtitle: { fontSize: '12px', color: 'var(--nexora-secondary-text)', margin: 0 },
  errorBox: { backgroundColor: 'var(--nexora-error-bg)', border: '1px solid var(--nexora-error-border)', color: 'var(--nexora-error-text)', padding: '10px', borderRadius: '12px', fontSize: '13px', textAlign: 'center', marginBottom: '16px' },
  inputGroup: { marginBottom: '16px' },
  label: { fontSize: '11px', fontWeight: '700', color: 'var(--nexora-label)', display: 'block', marginBottom: '6px' },
  prefix: { position: 'absolute', left: '14px', fontSize: '14px', fontWeight: '600', color: 'var(--nexora-secondary-text)', borderRight: '1px solid var(--nexora-prefix-border)', paddingRight: '10px' },
  toggleBtn: { position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: '12px', fontWeight: '600', color: 'var(--nexora-secondary-text)', cursor: 'pointer' },
  footer: { marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--nexora-divider)', textAlign: 'center' },
  footerText: { fontSize: '13px', color: 'var(--nexora-secondary-text)', margin: 0 },
  privacyLink: { display: 'inline-block', marginTop: '12px', color: 'var(--nexora-secondary-text)', fontSize: '12px', fontWeight: '600', textDecoration: 'underline', textUnderlineOffset: '3px' },
};

export default Login;